import { createStackNavigator } from '@react-navigation/stack';
import React, { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';

import { TaskInviteRequestButton } from '@components/Invite/TaskInviteRequestButton';
import { WebModal } from '@components/Modals/WebModal.web';
import { Box, ShadowBox, Text } from '@components/Restyle';
import Icon from '@components/shared/Icon/Icon';
import { WebIcon } from '@components/shared/Icon/WebIcon';
import { TaskPanelSkeleton } from '@components/shared/skeletons';
import CopyTaskDetails from '@components/Tasks/CopyTaskDetails';
import { EditTask } from '@components/Tasks/EditTask';
import TaskFormStack, { FormValues } from '@components/Tasks/TaskFormStack';
import { useWebDrawer } from '@components/Web/Drawer/WebDrawerContext';
import {
  GetTaskQuery,
  Project,
  Task,
  TaskPriority,
  TaskStatus,
  TaskUpdatedDocument,
  TaskUpdatedSubscription,
  TaskUpdatedSubscriptionVariables,
  useGetPublicTaskQuery,
  useGetTaskQuery,
  User,
} from '@graphql/generated';
import { clearModalStyle } from '@navigation/helpers';
import { TaskDetailTabs } from '@navigation/tasks/task-detail-tabs.web';
import { localDateFromUTCDate } from '@utils/formatters/date';

export type TaskPanelParamsList = {
  task: {
    taskId?: Task['id'];
  };
  create: undefined;
  'task-edit': { taskId: Task['id'] };
  'task-copy-modal': { taskId?: Task['id'] };
  'task-detail-edit-dates': {
    taskId?: Task['id'];
    fieldName: 'startDate' | 'dueDate';
  };
};

type TaskPanelProps = {
  externalNavigate?: any;
  idtask: string;
  isFromFullScreen?: boolean;
  isMaximize: boolean;
  onMaximize: () => void;
  onClose: () => void;
  onNavigation: (task: Task) => void;
  onPressCreator?: (task: Task) => void;
  onPressMember?: (item: User) => void;
  onPressProject?: (item: Project) => void;
  onNavigateToAccount?: () => void;
  onNavigateToProjects?: () => void;
};

const Stack = createStackNavigator<TaskPanelParamsList>();

const createTaskCreateHeader = (title: string, onClose: () => void) => () => (
  <Box flexDirection='row' backgroundColor='white'>
    <Box flex={1}>
      <Text variant='heading2'>{title}</Text>
    </Box>
    <Box alignItems='flex-end'>
      <Box flexDirection='row' accessibilityLabel='Close'>
        <WebIcon accessibilityLabel='Close' name='X' onPress={onClose} />
      </Box>
    </Box>
  </Box>
);

const createTaskEditHeader = (title: string, onClose: () => void) => () => (
  <Box flexDirection='row' backgroundColor='white'>
    <Box flex={1}>
      <Text variant='heading2'>{title}</Text>
    </Box>
    <Box alignItems='flex-end'>
      <Box flexDirection='row'>
        <WebIcon accessibilityLabel='Close' name='X' onPress={onClose} />
      </Box>
    </Box>
  </Box>
);

export const TaskPanel: React.FC<TaskPanelProps> = ({
  externalNavigate,
  idtask,
  isMaximize,
  onMaximize,
  onClose,
  onNavigation,
  onPressCreator,
  onPressMember,
  onPressProject,
  onNavigateToAccount,
  onNavigateToProjects,
  isFromFullScreen = false,
}) => {
  const { t } = useTranslation();
  const isFromMyTask = false;
  const taskId = idtask;

  // TA-10949 #24: the create-task header's X lives outside TaskForm.web's
  // Formik tree (see TaskForm.web.tsx's own comment on this prop). TaskForm
  // registers a "safe to close?" function here; when it hasn't (not yet
  // mounted, or the taskId!=='' edit screen, which doesn't use this ref),
  // fall back to closing immediately.
  const createFormAttemptCloseRef = useRef<(() => void) | null>(null);
  const handleCreateHeaderClose = () => {
    if (createFormAttemptCloseRef.current) {
      createFormAttemptCloseRef.current();
    } else {
      onClose();
    }
  };

  const {
    idProjectOfTaskDetailOpen,
    uuidProjectOfTaskDetailOpen,
    taskNameOfTaskDetailOpen,
    projectNameOfTaskDetailOpen,
  } = useWebDrawer();

  const { data, loading, subscribeToMore } = useGetTaskQuery({
    variables: {
      id: taskId || '',
    },
    skip: !taskId,
    fetchPolicy: 'cache-and-network',
  });

  useEffect(() => {
    if (!taskId) return;

    return subscribeToMore<
      TaskUpdatedSubscription,
      TaskUpdatedSubscriptionVariables
    >({
      document: TaskUpdatedDocument,
      variables: { taskId },
      updateQuery: (prev, { subscriptionData }) => {
        const updated = subscriptionData.data?.taskUpdated;
        if (!updated) return prev;
        return { ...prev, getTask: updated } as GetTaskQuery;
      },
    });
  }, [taskId, subscribeToMore]);

  const task = data?.getTask ?? null;
  const isNoAccess = !!taskId && !loading && !task;

  // getTask only resolves tasks the viewer is associated with, so the
  // no-access branch reads the viewer's pending join request from the public
  // task instead (TA-11014).
  const { data: publicTaskData } = useGetPublicTaskQuery({
    variables: { id: taskId },
    skip: !isNoAccess,
    fetchPolicy: 'cache-and-network',
  });
  const publicTask = publicTaskData?.getPublicTask;
  const hasPendingMemberAdditionRequest =
    publicTask?.__typename === 'PublicTask' &&
    publicTask.hasPendingMemberAdditionRequest;

  const editForm = (editStr: string) => {
    if (!task) return null;
    const {
      startDate: initialStartDate,
      dueDate: initialDueDate,
      dependencies,
      description: initialDescription,
      name: initialName,
    } = task;

    const startDate =
      initialStartDate && localDateFromUTCDate(initialStartDate);
    const dueDate = initialDueDate && localDateFromUTCDate(initialDueDate);

    const dependencyIds = dependencies?.map((d) => d.id) || [];

    const description = initialDescription || '';
    const name = initialName || '';

    const initialValues: FormValues = {
      ...(!!task && task),
      ...{ startDate, dueDate, name, description },
      dependencyIds,
      projectId: task?.projectId ?? '0',
      projectUUId: task?.project?.uuid ?? '0',
      users: (task?.members ?? []).map((m) => {
        return {
          id: m.user.id,
          avatar: m.user.avatar,
          name: m.user.name,
          firstName: m.user.firstName,
          lastName: m.user.lastName,
          role: m.role,
        };
      }),
      skillIds: task?.skillIds ?? [],
      phaseId: task?.phaseId ?? undefined,
      fixedOrder: task?.fixedOrder ?? false,
      recurring: task?.recurring ?? false,
      priority: task?.priority ?? TaskPriority.Medium,
      status: task?.status ?? TaskStatus.Ongoing,
      frequency: task?.frequency ?? undefined,
      completedAt: task?.completedAt ?? null,
    };
    return (
      <TaskFormStack
        taskId={taskId}
        initialValues={initialValues}
        isShortModal={true}
        editField={editStr}
      />
    );
  };

  if (taskId && taskId !== '' && loading && !task) {
    const skeleton = <TaskPanelSkeleton />;
    return isFromFullScreen ? (
      skeleton
    ) : (
      <ShadowBox key={taskId} flex={1} p='m' ml='m'>
        {skeleton}
      </ShadowBox>
    );
  }

  if (isNoAccess) {
    return (
      <ShadowBox key={taskId} flex={1} p='m' ml='m'>
        <Box flexDirection='row' backgroundColor='white' alignItems='center'>
          <Box flex={1}>
            <Box flexDirection='row' alignItems='center'>
              {projectNameOfTaskDetailOpen && (
                <Icon name='Folder' variant='xs' color='grey04' />
              )}
              <Text variant='webBodyHintSubline' color='grey04' ml='xxs'>
                {projectNameOfTaskDetailOpen || 'Task Details'}
              </Text>
            </Box>
          </Box>
          <Box alignItems='flex-end'>
            <Box flexDirection='row' accessibilityLabel='Close'>
              <WebIcon accessibilityLabel='Close' name='X' onPress={onClose} />
            </Box>
          </Box>
        </Box>
        <Box borderTopColor='grey02' borderTopWidth={1} mt='m' />
        {taskNameOfTaskDetailOpen && (
          <Box flexDirection='row' alignItems='center' mt='l'>
            <Text variant='labelLarge' color='textSecondary'>
              {taskNameOfTaskDetailOpen}
            </Text>
          </Box>
        )}
        <Box flex={1} justifyContent='center'>
          <TaskInviteRequestButton
            taskId={taskId}
            currentUserHasPendingMemberAdditionRequest={
              hasPendingMemberAdditionRequest
            }
          />
        </Box>
      </ShadowBox>
    );
  }

  const initialValues = {
    name: '',
    description: '',
    projectId: idProjectOfTaskDetailOpen ?? '',
    projectUUId: uuidProjectOfTaskDetailOpen ?? '',
    users: [],
    // TA-10949 #24: TaskForm.web's selectedUserList effect calls
    // setFieldValue('inviteEmails', ...) unconditionally on mount (even with
    // nothing selected). Without this key present from the start, that
    // first call adds a key Formik's initial snapshot never had, which
    // makes `dirty` true before the user has touched anything.
    inviteEmails: [],
    completedAt: undefined,
    skillIds: [],
    phaseId: undefined,
    dependencyIds: [],
    fixedOrder: false,
    recurring: false,
    priority: TaskPriority.Medium,
    status: TaskStatus.Ongoing,
    frequency: undefined,
    startDate: undefined,
    dueDate: undefined,
    checkList: [],
  };

  const page = () => (
    <Stack.Navigator>
      {taskId === '' && (
        <Stack.Screen
          name='task'
          getId={() => taskId}
          options={{
            header: createTaskCreateHeader(
              t('models:tasks.create.title'),
              handleCreateHeaderClose
            ),
          }}>
          {() => (
            <Box flex={1} backgroundColor='white'>
              <TaskFormStack
                screenName='Create'
                key={false}
                selectedList={[]}
                initialValues={initialValues}
                isShortModal={false}
                isAssignTask={false}
                chatId=''
                taskId=''
                isCopyTask={true}
                openModal={true}
                checkList={[]}
                attemptCloseRef={createFormAttemptCloseRef}
              />
            </Box>
          )}
        </Stack.Screen>
      )}
      {taskId !== '' && (
        <Stack.Screen
          name='task'
          getId={() => taskId}
          options={{ headerShown: false }}>
          {() => (
            <TaskDetailTabs
              externalNavigate={externalNavigate}
              isFromMyTask={isFromMyTask}
              task={task!}
              onClose={onClose}
              isMaximize={isMaximize}
              onMaximize={onMaximize}
              onPressCreator={onPressCreator}
              onPressMember={onPressMember}
              onPressProject={onPressProject}
              onNavigateToAccount={onNavigateToAccount}
              onNavigateToProjects={onNavigateToProjects}
            />
          )}
        </Stack.Screen>
      )}
      <Stack.Screen
        name='task-detail-edit-dates'
        options={{ ...clearModalStyle }}>
        {() => editForm('dates')}
      </Stack.Screen>
      <Stack.Screen
        name='task-edit'
        getId={() => taskId}
        options={{
          header: createTaskEditHeader(task?.name ?? '', onClose),
        }}>
        {() => (
          <Box flex={1} backgroundColor='white'>
            <EditTask taskId={taskId} />
          </Box>
        )}
      </Stack.Screen>
      <Stack.Screen
        name='task-copy-modal'
        options={{
          headerShown: false,
          presentation: 'transparentModal',
        }}>
        {({ navigation }) => (
          <WebModal
            visible
            title={t('shared:select')}
            width={504}
            onClose={navigation.goBack}>
            <CopyTaskDetails onComplete={onNavigation} />
          </WebModal>
        )}
      </Stack.Screen>
    </Stack.Navigator>
  );

  return isFromFullScreen ? (
    page()
  ) : (
    <ShadowBox key={taskId} flex={1} p='m' ml='m'>
      {page()}
    </ShadowBox>
  );
};
